/** Project a canonical ballistic contact through the resolved camera. The sight
 * remains on that exact world point; camera offset never becomes a fake pixel
 * offset. CSS-pixel sizing uses actual view depth and projection, including zoom.
 * Call after updating camera matrices. Invalid/behind-camera points hide safely. */
export function skyReticleProjection(camera, point, width, height) {
  if (
    ![point?.x, point?.y, point?.z, width, height].every(Number.isFinite) ||
    width <= 0 ||
    height <= 0
  )
    return { visible: false, scale: 0 };
  const e = camera.matrixWorldInverse.elements,
    p = camera.projectionMatrix.elements;
  const x = e[0] * point.x + e[4] * point.y + e[8] * point.z + e[12];
  const y = e[1] * point.x + e[5] * point.y + e[9] * point.z + e[13];
  const z = e[2] * point.x + e[6] * point.y + e[10] * point.z + e[14];
  const w = p[3] * x + p[7] * y + p[11] * z + p[15];
  if (!(w > 0)) return { visible: false, scale: 0 };
  const ndcX = (p[0] * x + p[4] * y + p[8] * z + p[12]) / w;
  const ndcY = (p[1] * x + p[5] * y + p[9] * z + p[13]) / w;
  const ndcZ = (p[2] * x + p[6] * y + p[10] * z + p[14]) / w;
  const diameter = width < 500 ? 36 : 40;
  return {
    visible: Math.abs(ndcX) <= 1 && Math.abs(ndcY) <= 1 && Math.abs(ndcZ) <= 1,
    x: ((ndcX + 1) * width) / 2,
    y: ((1 - ndcY) * height) / 2,
    ndcX,
    ndcY,
    depth: -z,
    diameter,
    // Outer ticks span0.86 local units; use camera-space depth, not Euclidean
    // distance or an obsolete fixed camera position, to keep exact pixel size.
    scale: (diameter * 2 * w) / (height * p[5] * 0.86),
  };
}

/** A camera-facing open sight with dark/light outlines. All resources are handed
 * to the renderer's existing owner; updates allocate no geometry or materials. */
export function createSkyReticle(T, own) {
  const object = new T.Group();
  const overlay = new T.Scene();
  overlay.add(object);
  object.name = 'sky-ballistic-reticle';
  const material = (color) =>
    own(
      new T.MeshBasicMaterial({
        color,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
        transparent: true,
        opacity: 0.98,
      }),
    );
  const dark = material('#06121b'),
    light = material('#e9fffa');
  function ticks(thickness, outset) {
    const vertices = [];
    const rectangle = (x0, y0, x1, y1) =>
      vertices.push(x0, y0, 0, x1, y0, 0, x1, y1, 0, x0, y0, 0, x1, y1, 0, x0, y1, 0);
    for (const side of [-1, 1]) {
      const a = side * 0.31,
        b = side * 0.43;
      rectangle(Math.min(a, b) - outset, -thickness / 2, Math.max(a, b) + outset, thickness / 2);
      rectangle(-thickness / 2, Math.min(a, b) - outset, thickness / 2, Math.max(a, b) + outset);
    }
    return own(
      new T.BufferGeometry().setAttribute('position', new T.Float32BufferAttribute(vertices, 3)),
    );
  }
  for (const [geometry, paint, order] of [
    [own(new T.RingGeometry(0.185, 0.29, 48)), dark, 30],
    [ticks(0.075, 0.018), dark, 30],
    [own(new T.RingGeometry(0.215, 0.26, 48)), light, 31],
    [ticks(0.035, 0), light, 31],
  ]) {
    const mesh = new T.Mesh(geometry, paint);
    mesh.renderOrder = order;
    object.add(mesh);
  }
  const ballisticGlyph = [...object.children];
  const contactMarker = new T.Group();
  contactMarker.name = 'sky-aim-impact-marker';
  contactMarker.visible = false;
  for (const [radius, paint, order] of [
    [0.14, dark, 34],
    [0.085, material('#ffad64'), 35],
  ]) {
    const mesh = new T.Mesh(own(new T.CircleGeometry(radius, 20)), paint);
    mesh.renderOrder = order;
    contactMarker.add(mesh);
  }
  object.add(contactMarker);
  // Brackets identify the actual opening even while the cannon aims elsewhere.
  // Their broken outline is a readiness cue, not a larger collision aperture.
  const readiness = new T.Group(),
    open = new T.Group(),
    closed = new T.Group();
  readiness.name = 'sky-guardian-readiness';
  readiness.add(open, closed);
  object.add(readiness);
  readiness.visible = false;
  const cue = material('#8affcf');
  const arc = own(new T.RingGeometry(0.37, 0.43, 12, 1, Math.PI / 12, Math.PI / 3));
  const border = own(new T.RingGeometry(0.33, 0.47, 12, 1, Math.PI / 12, Math.PI / 3));
  const bar = own(new T.PlaneGeometry(0.42, 0.065));
  const barBorder = own(new T.PlaneGeometry(0.49, 0.14));
  for (let i = 0; i < 4; i++) {
    for (const [geometry, paint, order] of [
      [border, dark, 32],
      [arc, cue, 33],
    ]) {
      const mesh = new T.Mesh(geometry, paint);
      mesh.rotation.z = (i * Math.PI) / 2;
      mesh.renderOrder = order;
      open.add(mesh);
    }
  }
  for (const angle of [-Math.PI / 4, Math.PI / 4]) {
    for (const [geometry, paint, order] of [
      [barBorder, dark, 32],
      [bar, cue, 33],
    ]) {
      const mesh = new T.Mesh(geometry, paint);
      mesh.rotation.z = angle;
      mesh.renderOrder = order;
      closed.add(mesh);
    }
  }
  const relative = new T.Vector3(),
    inverse = new T.Quaternion();
  let previousGuardian = null,
    wasOpen = false,
    openedAt = -Infinity,
    priorTime = -Infinity;
  function updateReadiness(point, camera, width, height, guardian, time, reduced) {
    const target = guardian?.weakpoint;
    const visible = guardian && !guardian.defeated && guardian.age >= 4.5 && target;
    const targetFrame = visible ? skyReticleProjection(camera, target, width, height) : null;
    readiness.visible = !!targetFrame?.visible;
    const ready = !!(visible && guardian.vulnerable && target.exposed);
    if (guardian?.id !== previousGuardian || time < priorTime) wasOpen = false;
    if (ready && !wasOpen) openedAt = time;
    wasOpen = ready;
    previousGuardian = guardian?.id;
    priorTime = time;
    const aligned =
      ready &&
      point.targetId === guardian.id &&
      Math.abs(point.z - target.z) < 1e-4 &&
      Math.hypot(point.x - target.x, point.y - target.y) <= target.radius;
    object.userData.ready = aligned;
    if (!readiness.visible) return aligned;
    const age = Math.max(0, time - openedAt);
    const pulse = ready && !reduced && age < 0.35 ? Math.sin((Math.PI * age) / 0.35) * 0.18 : 0;
    relative.set(
      target.x - object.position.x,
      target.y - object.position.y,
      target.z - object.position.z,
    );
    inverse.copy(object.quaternion).invert();
    readiness.position.copy(relative.applyQuaternion(inverse)).divideScalar(object.scale.x);
    // Fixed40/44px brackets remain readable on a phone, independent of depth.
    readiness.scale.setScalar((targetFrame.scale / object.scale.x) * 1.12 * (1 + pulse));
    open.visible = ready;
    closed.visible = !ready;
    cue.color.set(ready ? '#8affcf' : '#ffad64');
    readiness.userData.state = ready ? 'open' : 'closed';
    return aligned;
  }
  function update(
    point,
    camera,
    width,
    height,
    {
      impact = 0,
      confidence = 0,
      reduced = false,
      guardian = null,
      time = 0,
      aimRequest = null,
      assisted = false,
      weaponsSafe = false,
    } = {},
  ) {
    camera.updateMatrixWorld(true);
    const projected = skyReticleProjection(camera, point, width, height);
    const requestFrame = aimRequest
      ? skyReticleProjection(camera, aimRequest, width, height)
      : null;
    const display = requestFrame?.visible ? aimRequest : point;
    const displayFrame = requestFrame?.visible ? requestFrame : projected;
    const anchor = displayFrame.visible ? display : guardian?.weakpoint;
    const anchorFrame = displayFrame.visible
      ? displayFrame
      : skyReticleProjection(camera, anchor, width, height);
    object.visible = anchorFrame.visible;
    object.userData.ready = false;
    for (const mesh of ballisticGlyph) mesh.visible = displayFrame.visible;
    contactMarker.visible = false;
    if (!anchorFrame.visible) {
      readiness.visible = false;
      return projected;
    }
    object.position.set(anchor.x, anchor.y, anchor.z);
    object.quaternion.copy(camera.quaternion);
    object.scale.setScalar(
      anchorFrame.scale * (reduced ? 1 : 1 + Math.max(0, Math.min(1, impact)) * 0.08),
    );
    const ready = updateReadiness(point, camera, width, height, guardian, time, reduced);
    if (
      requestFrame?.visible &&
      projected.visible &&
      Math.hypot(projected.x - requestFrame.x, projected.y - requestFrame.y) > 6
    ) {
      relative.set(point.x - anchor.x, point.y - anchor.y, point.z - anchor.z);
      contactMarker.position
        .copy(relative.applyQuaternion(inverse.copy(object.quaternion).invert()))
        .divideScalar(object.scale.x);
      contactMarker.scale.setScalar(projected.scale / object.scale.x);
      contactMarker.visible = true;
    }
    object.visible = displayFrame.visible || readiness.visible;
    if (!projected.visible) object.userData.ready = false;
    object.userData.assisted = assisted;
    object.userData.weaponsSafe = weaponsSafe;
    // A broken sight signals weapons safe; engaged assistance rotates the
    // outer ticks so its state remains distinguishable without colour.
    ballisticGlyph[1].rotation.z = ballisticGlyph[3].rotation.z = assisted ? Math.PI / 4 : 0;
    ballisticGlyph[2].visible = displayFrame.visible && !weaponsSafe;
    light.color.set(
      weaponsSafe
        ? '#ffcc84'
        : ready
          ? '#8affcf'
          : impact > 0
            ? '#ffdc80'
            : assisted || confidence > 0
              ? '#8affcf'
              : '#e9fffa',
    );
    return projected;
  }
  // Scene bloom runs before this layer: otherwise a bright crest washes the dark
  // outline back out, regardless of transparent mesh renderOrder.
  function drawOverlay(renderer, camera) {
    if (!object.visible) return;
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    try {
      renderer.clearDepth();
      renderer.render(overlay, camera);
    } finally {
      renderer.autoClear = autoClear;
    }
  }
  return { object, update, drawOverlay };
}
